import { useState, useContext, useEffect, useMemo, useRef } from 'react'
import { PassContext } from '../pass-context'
import { flatten } from "flat"
import _ from "lodash"


import { TextField, Typography, Button, FormControlLabel, FormLabel, RadioGroup, Radio, Switch, Stack, Tooltip } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import dataSelector from './data-selector'
import InstanceDataSelector from '/utils/instance-data-selector'
import findPathByValue from '/utils/find-path-by-value'
import PopupShell from '../popup-shell'

const hasTrimmedValue = value => {
  if (typeof value !== "string") return false
  return value.trim().length > 0
}

const PopupDialogText = ({ open, close, title, sources, path, required }) => {

  const { classData, objectData, instanceData, setClassData, setObjectData, setGoogleBoth, scannerInstanceData, openInstanceDataManager } = useContext(PassContext)

  const [newClassData, setNewClassData] = useState({})
  const [newObjectData, setNewObjectData] = useState({})
  const [sourcePaths, setSourcePaths] = useState([])
  const [dataSource, setDataSource] = useState("class")

  const dataChanged = useMemo(() => {
    if (open) {
      if (!_.isEqual(objectData, newObjectData)) return true
      if (!_.isEqual(classData, newClassData)) return true
    }

    return false
  }, [newClassData, newObjectData])

  const templateSource = sources?.[0]

  const editedValue = useMemo(() => {
    if (dataSource === "instance") return _.get(newObjectData, path)
    return templateSource === "object" ? _.get(newObjectData, path) : _.get(newClassData, path)
  }, [dataSource, newClassData, newObjectData, path, templateSource])

  const showRequiredError = required && !hasTrimmedValue(editedValue)

  // Runs on "open" state change or if template / instanceData change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewClassData(_.cloneDeep(classData))
        setNewObjectData(_.cloneDeep(objectData))

        let objectValue = _.get(objectData, path)
        let source = "template"
        if (sources?.includes("object")) source = objectValue?.match(/{{.*}}/) ? "instance" : "template"

        setDataSource(source)
      }
    }
    catch (err) {
      throw err
    }
  }, [open, sources, path, classData, objectData])

  // Refresh our template / instance data source paths if instancedata changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten({ ...instanceData, __scanner_data__:{...scannerInstanceData} })).map(p => `{{${p}}}`).sort())
  }, [instanceData, scannerInstanceData])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const saveFormData = event => {
    event.preventDefault() // Prevents a "submit" which reloads the page

    if (showRequiredError) return

    // Save content of our "new" template/instanceData back to state
    // We need to use googleBoth rather than separate googleClass and googleObject if we are saving both data objects
    if (!_.isEqual(newClassData, classData) && !_.isEqual(newObjectData, objectData)) {
      setGoogleBoth({
        googleClass: newClassData,
        googleObject: newObjectData
      })
    } else {
      if (!_.isEqual(newClassData, classData)) setClassData(newClassData)
      if (!_.isEqual(newObjectData, objectData)) setObjectData(newObjectData)
    }
    close()
  }

  const handleChangeDataSource = value => {
    setDataSource(value)

    const newObjectValue = (value === "template") ? dataSelector({ objectData: newObjectData, path, instanceData, scannerInstanceData }) : findPathByValue(instanceData, _.get(newObjectData, path))

    setNewObjectData(prev => {
      let nd = { ...prev }
      _.set(nd, path, newObjectValue)
      return nd
    })
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose}>

        <div className="flex flex-col h-full gap-2">

          <div className="flex flex-row justify-between mt-5">
            <Typography color="secondary" variant="h6">{title}</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>

          <hr />

          <div className='mt-5 overflow-y-scroll flex flex-col gap-5 mb-[100px]'>

            <FormLabel id="data_type_label">Data Source</FormLabel>
            <RadioGroup
              row
              aria-labelledby="data_type_label"
              value={dataSource}
              onChange={e => handleChangeDataSource(e.target.value)}
            >
              {sources?.includes("class") && <FormControlLabel value="template" control={<Radio />} label="Template (fixed for all passes)" />}
              {sources?.includes("object") && <FormControlLabel value="template" control={<Radio />} label="Template (fixed for all passes)" />}
              {sources?.includes("object") && <FormControlLabel value="instance" control={<Radio />} label="Instance (unique for each pass)" />}
            </RadioGroup>

            {dataSource === "template" && <DataSourceTemplate path={path} title={title} templateSource={templateSource} newClassData={newClassData} setNewClassData={setNewClassData} newObjectData={newObjectData} setNewObjectData={setNewObjectData} required={required} showRequiredError={showRequiredError} source={sources} />}
            {dataSource === "instance" && <DataSourceInstance path={path} title={title} newObjectData={newObjectData} setNewObjectData={setNewObjectData} instanceData={instanceData} sourcePaths={sourcePaths} required={required} scannerInstanceData={scannerInstanceData} showRequiredError={showRequiredError} />}

          </div>

          <div className='fixed bottom-5 w-[460px]'>
            <hr className='mb-5' />
            <div className="flex flex-row gap-5 justify-between">
              <Button startIcon={<DatasetOutlinedIcon />} onClick={openInstanceDataManager} >Edit sample data</Button>
              <Button variant="contained" disabled={!dataChanged || showRequiredError} onClick={saveFormData}>Save</Button>
            </div>
          </div>

        </div>
      </PopupShell>
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogText


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////

const DataSourceTemplate = ({ path, title, templateSource, newClassData, setNewClassData, newObjectData, setNewObjectData, required, showRequiredError }) => {

  const value = templateSource === "object" ? _.get(newObjectData, path) : _.get(newClassData, path)

  const onChangeHandler = value => {
    templateSource === "object" ? setNewObjectData(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)

      if (/defaultValue/.test(path)) { // This is a localised value. Must add language...
        const languagePath = path.replace(/value/,"language")
        _.set(nd,languagePath,"en-US")
      }

      return nd
    }) : setNewClassData(prev => {
      let nc = { ...prev }
      _.set(nc, path, value)

      if (/defaultValue/.test(path)) { // This is a localised value. Must add language...
        const languagePath = path.replace(/value/,"language")
        _.set(nc,languagePath,"en-US")
      }      
      return nc
    })
  }

  return (
    <TextField label={title}
      value={value ?? ""}
      slotProps={{ inputLabel: { shrink: true } }}
      type="search"
      required={required}
      error={showRequiredError}
      onChange={e => { onChangeHandler(e.target.value) }}
      helperText={showRequiredError ? "A value is required." : undefined}
    />
  )
}

const DataSourceInstance = ({ newObjectData, path, setNewObjectData, instanceData, sourcePaths, required, scannerInstanceData, showRequiredError }) => {
  try {
    let templateValue = _.get(newObjectData, path)
    let strippedValue = templateValue.replace(/{{[^}]*}}/, "")

    const [expertMode, setExpertMode] = useState(strippedValue.length > 0)

    const changeEditMode = mode => {
      if (!mode) {
        let newValue = _.get(newObjectData, path)?.replace(/.*({{[^}]*}}).*/, "$1")
        if (!/^{{.*}}$/.test(newValue)) newValue = ""

        setNewObjectData(prev => {
          let nd = { ...prev }
          _.set(nd, path, newValue)

          if (/defaultValue/.test(path)) { // This is a localised value. Must add language...
            const languagePath = path.replace(/value/,"language")
            _.set(nd,languagePath,"en-US")
          }

          return nd
        })
      }

      setExpertMode(mode)
    }

    if (typeof templateValue !== "undefined") return (
      <>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>Edit mode : Simple selection</Typography>
          <Switch checked={expertMode} onChange={e => changeEditMode(e.target.checked)} />
          <Typography>Expert mode</Typography>
        </Stack>

        <SimpleSelection active={!expertMode} newObjectData={newObjectData} path={path} instanceData={instanceData} setNewObjectData={setNewObjectData} sourcePaths={sourcePaths} required={required} showRequiredError={showRequiredError} />
        <ExpertSelection active={expertMode} newObjectData={newObjectData} path={path} instanceData={instanceData} setNewObjectData={setNewObjectData} required={required} scannerInstanceData={scannerInstanceData} sourcePaths={sourcePaths} showRequiredError={showRequiredError}/>
      </>
    )
  }
  catch (err) {
    throw err
  }
}

const SimpleSelection = ({ active, path, newObjectData, instanceData, setNewObjectData, scannerInstanceData, sourcePaths, required, showRequiredError }) => {

  const templateValue = _.get(newObjectData, path)
  const instanceDataValue = dataSelector({ objectData: newObjectData, instanceData, scannerInstanceData, path })

  const changeHandler = value => {
    setNewObjectData(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)

      if (/defaultValue/.test(path)) { // This is a localised value. Must add language...
        const languagePath = path.replace(/value/,"language")
        _.set(nd,languagePath,"en-US")
      }

      return nd
    })
  }

  if (active && typeof instanceDataValue !== "undefined") return (<>
    <InstanceDataSelector updateValue={changeHandler} sourcePaths={sourcePaths} value={templateValue} dataSelector={dataSelector} instanceData={instanceData} scannerInstanceData={scannerInstanceData} />

    <TextField label="Sample Value"
      value={instanceDataValue ?? ""}
      slotProps={{ inputLabel: { shrink: true } }}
      type="search"
      disabled={true}
      required={required}
      error={showRequiredError}
      helperText={showRequiredError ? "A value is required." : undefined}
      fullWidth
    />
  </>
  )
}

const ExpertSelection = ({ active, newObjectData, setNewObjectData, instanceData, scannerInstanceData, path, sourcePaths, showRequiredError }) => {

  const inputRef = useRef(null)
  
  const value = _.get(newObjectData, path)

  const resolvedDataValue = dataSelector({ objectData: newObjectData, instanceData, scannerInstanceData, path })

  const changeHandler = value => {
    setNewObjectData(prev => {
      let nd = { ...prev }
      _.set(nd, path, value)

      if (/defaultValue/.test(path)) { // This is a localised value. Must add language...
        const languagePath = path.replace(/value/,"language")
        _.set(nd,languagePath,"en-US")
      }
            
      return nd
    })
  }

  const insertAtCursor = insertText => {
    const input = inputRef.current
    if (!input) return

    const start = input.selectionStart
    const end = input.selectionEnd

    // New value with inserted text
    const newValue = value.substring(0, start) + insertText + value.substring(end)

    changeHandler(newValue)

    // Update cursor position after insertion
    requestAnimationFrame(() => {
      input.setSelectionRange(start + insertText.length, start + insertText.length)
      input.focus()
    })
  }

  if (active) return (
    <>
      <InstanceDataSelector updateValue={insertAtCursor} sourcePaths={sourcePaths} value={"choose"} dataSelector={dataSelector} instanceData={instanceData} scannerInstanceData={scannerInstanceData} limitedEditingOnly={false} />
    
      <TextField
        inputRef={inputRef}
        label="Value"
        slotProps={{ inputLabel: { shrink: true } }}
        value={value}
        error={showRequiredError}
        helperText={showRequiredError ? "A value is required." : undefined}
        onChange={e => changeHandler(e.target.value)}
        fullWidth
      />

      <TextField
        label="Resolved value"
        value={resolvedDataValue}
        slotProps={{ inputLabel: { shrink: true } }}
        disabled={true}
        fullWidth
      />
    </>
  )
}
